import React, { useState } from "react";
import PropTypes from "prop-types";
import "./OemAdminTargetingMmyPopUp.css";

const resolveCheckedState = (checkedItems, key, isItemExcluded) => {
  return checkedItems[key] === undefined
    ? !isItemExcluded(key)
    : checkedItems[key];
};

const collectKeysForModel = (makeName, modelName, getAllYearsForModel) => {
  return getAllYearsForModel(makeName, modelName).map(
    (year) => `${makeName}_${modelName}_${year}`
  );
};

const collectKeysForMake = (make, getAllYearsForModel) => {
  return (make.models || []).flatMap((model) =>
    collectKeysForModel(make.make, model.name, getAllYearsForModel)
  );
};

const forEachMmyKey = (mmyData, getAllYearsForModel, callback) => {
  mmyData.forEach((make) => {
    (make.models || []).forEach((model) => {
      collectKeysForModel(make.make, model.name, getAllYearsForModel).forEach(
        callback
      );
    });
  });
};

const parseYear = (year) => {
  const parsedYear = Number.parseInt(year, 10);
  return Number.isNaN(parsedYear) ? null : parsedYear;
};

export default function OemAdminTargetingMmyPopUp({
  mmyData = [],
  mmyInclusion,
  mmyExclusion,
  mmySave,
  onClose,
  reviewStatus,
}) {
  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});

  // Helper function to check if an item should be unchecked based on mmyExclusion
  const isItemExcluded = React.useCallback(
    (key) => {
      const excluded =
        mmyExclusion &&
        Array.isArray(mmyExclusion) &&
        mmyExclusion.includes(key);
      return excluded;
    },
    [mmyExclusion]
  );

  // Helper function to get checkbox state for a make (all its models/years)
  const getMakeCheckboxState = (make) => {
    const allKeys = collectKeysForMake(make, getAllYearsForModel);

    const checkedCount = allKeys.filter((key) => {
      return resolveCheckedState(checkedItems, key, isItemExcluded);
    }).length;

    return {
      checked: checkedCount === allKeys.length && allKeys.length > 0,
      indeterminate: checkedCount > 0 && checkedCount < allKeys.length,
    };
  };

  // Helper function to get checkbox state for a model (all its years)
  const getModelCheckboxState = (makeName, model) => {
    const allKeys = collectKeysForModel(makeName, model.name, getAllYearsForModel);

    const checkedCount = allKeys.filter((key) => {
      return resolveCheckedState(checkedItems, key, isItemExcluded);
    }).length;

    return {
      checked: checkedCount === allKeys.length && allKeys.length > 0,
      indeterminate: checkedCount > 0 && checkedCount < allKeys.length,
    };
  };

  // Handle make checkbox change
  const handleMakeCheckboxChange = (make) => {
    const allKeys = collectKeysForMake(make, getAllYearsForModel);

    const { checked } = getMakeCheckboxState(make);
    const newState = !checked;

    setCheckedItems((prev) => {
      const updated = { ...prev };
      allKeys.forEach((key) => {
        updated[key] = newState;
      });
      return updated;
    });
  };

  // Handle model checkbox change
  const handleModelCheckboxChange = (makeName, model) => {
    const allKeys = collectKeysForModel(makeName, model.name, getAllYearsForModel);

    const { checked } = getModelCheckboxState(makeName, model);
    const newState = !checked;

    setCheckedItems((prev) => {
      const updated = { ...prev };
      allKeys.forEach((key) => {
        updated[key] = newState;
      });
      return updated;
    });
  };

  const handleSave = () => {
    // Build complete state of all MMY items
    const allItems = {};

    // Go through all makes, models, and years to build the complete state
    forEachMmyKey(enhancedMmyData, getAllYearsForModel, (key) => {
      allItems[key] = resolveCheckedState(checkedItems, key, isItemExcluded);
    });

    const includedMMY = Object.entries(allItems)
      .filter(([_, checked]) => checked)
      .map(([key]) => key);

    const excludedMMY = Object.entries(allItems)
      .filter(([_, checked]) => !checked)
      .map(([key]) => key);

    mmySave([
      { key: "mmy_inclusion", values: includedMMY },
      { key: "mmy_exclusion", values: excludedMMY },
    ]);

    onClose();
  };

  // Helper function to check if all items are unchecked (save button should be disabled)
  const areAllItemsUnchecked = () => {
    let hasAnyCheckedItem = false;

    forEachMmyKey(enhancedMmyData, getAllYearsForModel, (key) => {
      if (resolveCheckedState(checkedItems, key, isItemExcluded)) {
        hasAnyCheckedItem = true;
      }
    });

    return !hasAnyCheckedItem;
  };

  const handleClearAll = () => {
    const clearedItems = {};
    forEachMmyKey(enhancedMmyData, getAllYearsForModel, (key) => {
      clearedItems[key] = false;
    });
    setCheckedItems(clearedItems);
  };

  const handleCancel = () => {
    // Cancel functionality
    onClose();
  };

  // Helper function to get all years for a specific make/model (including from exclusions and inclusions)
  const getAllYearsForModel = React.useCallback(
    (makeName, modelName) => {
      const makeObj = mmyData.find((make) => make.make === makeName);
      const modelObj = (makeObj?.models || []).find(
        (model) => model.name === modelName
      );

      // Start with years from mmyData
      let allYears = new Set(modelObj?.years || []);

      // Add years from mmyExclusion that match this make/model
      if (mmyExclusion && Array.isArray(mmyExclusion)) {
        mmyExclusion.forEach((exclusionItem) => {
          if (exclusionItem.includes("_")) {
            const [make, model, year] = exclusionItem.split("_");
            const parsedYear = parseYear(year);
            if (
              make === makeName &&
              model === modelName &&
              parsedYear !== null
            ) {
              allYears.add(parsedYear);
            }
          }
        });
      }

      // Add years from mmyInclusion that match this make/model
      if (mmyInclusion && Array.isArray(mmyInclusion)) {
        mmyInclusion.forEach((inclusionItem) => {
          if (inclusionItem.includes("_") && inclusionItem !== "ALL_ALL_ALL") {
            const [make, model, year] = inclusionItem.split("_");
            const parsedYear = parseYear(year);
            if (
              make === makeName &&
              model === modelName &&
              parsedYear !== null
            ) {
              allYears.add(parsedYear);
            }
          }
        });
      }

      return Array.from(allYears).sort((a, b) => a - b);
    },
    [mmyData, mmyExclusion, mmyInclusion]
  );

  // Use enhanced data for rendering - moved to useMemo to avoid dependency issues
  const enhancedMmyData = React.useMemo(() => {
    const enhancedData = [...mmyData];
    const existingMakes = new Map();

    // Index existing data
    mmyData.forEach((make) => {
      const modelsMap = new Map();
      (make.models || []).forEach((model) => {
        modelsMap.set(model.name, new Set(model.years || []));
      });
      existingMakes.set(make.make, modelsMap);
    });

    // Process both inclusions and exclusions to find additional makes/models/years
    const allMmyItems = [...(mmyInclusion || []), ...(mmyExclusion || [])];

    allMmyItems.forEach((item) => {
      if (item.includes("_") && item !== "ALL_ALL_ALL") {
        const [makeName, modelName, year] = item.split("_");
        const parsedYear = parseYear(year);
        if (makeName && modelName && parsedYear !== null) {
          // Check if make exists
          if (!existingMakes.has(makeName)) {
            // Add new make
            existingMakes.set(makeName, new Map());
            enhancedData.push({
              make: makeName,
              models: [],
            });
          }

          const makeIndex = enhancedData.findIndex(
            (make) => make.make === makeName
          );
          const modelsMap = existingMakes.get(makeName);

          // Check if model exists under this make
          if (!modelsMap.has(modelName)) {
            // Add new model
            modelsMap.set(modelName, new Set());
            enhancedData[makeIndex].models.push({
              name: modelName,
              years: [],
            });
          }

          // Add year to the model
          const modelIndex = enhancedData[makeIndex].models.findIndex(
            (model) => model.name === modelName
          );
          const yearsSet = modelsMap.get(modelName);
          yearsSet.add(parsedYear);

          // Update the years array
          enhancedData[makeIndex].models[modelIndex].years = Array.from(
            yearsSet
          ).sort((a, b) => a - b);
        }
      }
    });

    return enhancedData;
  }, [mmyData, mmyInclusion, mmyExclusion]);

  // Helper function to get all years for current make/model (including from exclusions)
  const getAllYearsForCurrentModel = () => {
    return getAllYearsForModel(selectedMake, selectedModel);
  };

  const currentMake = enhancedMmyData.find(
    (make) => make.make === selectedMake
  );

  // Initialize selectedMake and selectedModel with enhanced data if not set
  React.useEffect(() => {
    if (!selectedMake && enhancedMmyData.length > 0) {
      setSelectedMake(enhancedMmyData[0].make);
      setSelectedModel((enhancedMmyData[0]?.models || [])[0]?.name || null);
    }
  }, [enhancedMmyData, selectedMake]);

  // Initialize checkbox states based on inclusion/exclusion data
  React.useEffect(() => {
    if (enhancedMmyData.length > 0) {
      const initialState = {};

      // Build initial state for all items
      const hasSpecificInclusions =
        Array.isArray(mmyInclusion) &&
        mmyInclusion.length > 0 &&
        !mmyInclusion.includes("ALL_ALL_ALL");

      forEachMmyKey(enhancedMmyData, getAllYearsForModel, (key) => {
        let shouldBeChecked;

        if (hasSpecificInclusions) {
          const [keyMake] = key.split("_");
          const makeAllPattern = `${keyMake}_ALL_ALL`;
          shouldBeChecked =
            mmyInclusion.includes(key) || mmyInclusion.includes(makeAllPattern);
        } else {
          shouldBeChecked = !isItemExcluded(key);
        }

        if (Array.isArray(mmyExclusion) && mmyExclusion.includes(key)) {
          shouldBeChecked = false;
        }

        initialState[key] = shouldBeChecked;
      });

      setCheckedItems(initialState);
    }
  }, [
    mmyInclusion,
    mmyExclusion,
    enhancedMmyData,
    getAllYearsForModel,
    isItemExcluded,
  ]);

  return (
    <div
      className="mmy-popup-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        className="mmy-popup"
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
          width: "800px",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          className="mmy-popup-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            backgroundColor: "#f8f9fa",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "600",
              color: "#2c3e50",
            }}
          >
            Targeting - Make/Model/Year
          </h2>
          <button
            onClick={onClose}
            className="mmy-close-button"
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              color: "#6c757d",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          className="mmy-popup-content"
          style={{
            display: "flex",
            gap: "12px",
            padding: "0",
            overflow: "hidden",
            border: "none",
            borderTop: "none",
            borderBottom: "none",
          }}
        >
          {/* Make Section */}
          <div
            className="mmy-section"
            style={{
              flex: 1,
              backgroundColor: "#F3F3F3",
              display: "flex",
              flexDirection: "column",
              maxHeight: "400px",
              borderRadius: "8px",
              border: "none",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                backgroundColor: "#F3F3F3",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#2c3e50",
                }}
              >
                Make ({enhancedMmyData.length})
              </h3>
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "12px 20px 16px 20px",
                backgroundColor: "#F3F3F3",
              }}
            >
              {enhancedMmyData.map((make) => (
                <button
                  key={make.make}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    margin: "4px 0",
                    backgroundColor: "transparent",
                    borderRadius: "4px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    border: "none",
                    width: "100%",
                    textAlign: "left",
                  }}
                  onClick={() => {
                    setSelectedMake(make.make);
                    setSelectedModel((make.models || [])[0]?.name || null);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={getMakeCheckboxState(make).checked}
                    ref={(input) => {
                      if (input)
                        input.indeterminate =
                          getMakeCheckboxState(make).indeterminate;
                    }}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleMakeCheckboxChange(make);
                    }}
                    style={{
                      marginRight: "12px",
                      width: "18px",
                      height: "18px",
                      accentColor: "#2196f3",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#2c3e50",
                      fontWeight: selectedMake === make.make ? "500" : "400",
                    }}
                  >
                    {make.make}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Model Section */}
          <div
            className="mmy-section"
            style={{
              flex: 1,
              backgroundColor: "#F3F3F3",
              display: "flex",
              flexDirection: "column",
              maxHeight: "400px",
              borderRadius: "8px",
              border: "none",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                backgroundColor: "#F3F3F3",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#2c3e50",
                }}
              >
                Model ({(currentMake?.models || []).length})
              </h3>
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "12px 20px 16px 20px",
                backgroundColor: "#F3F3F3",
              }}
            >
              {(currentMake?.models || []).map((model) => (
                <button
                  key={model.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    margin: "4px 0",
                    backgroundColor: "transparent",
                    borderRadius: "4px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    border: "none",
                    width: "100%",
                    textAlign: "left",
                  }}
                  onClick={() => setSelectedModel(model.name)}
                >
                  <input
                    type="checkbox"
                    checked={getModelCheckboxState(selectedMake, model).checked}
                    ref={(input) => {
                      if (input)
                        input.indeterminate = getModelCheckboxState(
                          selectedMake,
                          model
                        ).indeterminate;
                    }}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleModelCheckboxChange(selectedMake, model);
                    }}
                    style={{
                      marginRight: "12px",
                      width: "18px",
                      height: "18px",
                      accentColor: "#2196f3",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#2c3e50",
                      fontWeight: selectedModel === model.name ? "500" : "400",
                    }}
                  >
                    {model.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Year Section */}
          <div
            className="mmy-section"
            style={{
              flex: 1,
              backgroundColor: "#F3F3F3",
              display: "flex",
              flexDirection: "column",
              maxHeight: "400px",
              borderRadius: "8px",
              border: "none",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                backgroundColor: "#F3F3F3",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#2c3e50",
                }}
              >
                Year ({getAllYearsForCurrentModel().length})
              </h3>
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "12px 20px 16px 20px",
                backgroundColor: "#F3F3F3",
              }}
            >
              {getAllYearsForCurrentModel().map((year) => {
                const key = `${selectedMake}_${selectedModel}_${year}`;
                const isExcluded = isItemExcluded(key);
                const isChecked =
                  checkedItems[key] === undefined
                    ? !isExcluded
                    : checkedItems[key];

                return (
                  <div
                    key={year}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 12px",
                      margin: "4px 0",
                      backgroundColor: "transparent",
                      borderRadius: "4px",
                      transition: "all 0.2s",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {
                        setCheckedItems((prev) => ({
                          ...prev,
                          [key]: !isChecked,
                        }));
                      }}
                      style={{
                        marginRight: "12px",
                        width: "18px",
                        height: "18px",
                        accentColor: "#2196f3",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#2c3e50",
                      }}
                    >
                      {year}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="mmy-popup-footer"
          style={{
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "white",
          }}
        >
          <button
            onClick={handleClearAll}
            className="mmy-clear-button"
            style={{
              padding: "10px 0",
              border: "none",
              borderRadius: "6px",
              backgroundColor: "transparent",
              color: "#6c757d",
              cursor: "pointer",
              fontSize: "15px",
              fontWeight: "400",
              textDecoration: "none",
            }}
          >
            Clear All
          </button>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={handleCancel}
              style={{
                padding: "10px 24px",
                border: "1px solid #d0d7de",
                borderRadius: "6px",
                backgroundColor: "#1214151A",
                color: "#2A2C2D",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.2s",
                width: "114px",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={areAllItemsUnchecked()}
              style={{
                padding: "10px 24px",
                border: "none",
                borderRadius: "6px",
                backgroundColor: areAllItemsUnchecked()
                  ? "#1214151A"
                  : "#0969da",
                color: areAllItemsUnchecked() ? "#2A2C2D" : "#ffffff", // Fixed: Use #2A2C2D when disabled
                cursor: areAllItemsUnchecked() ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.2s",
                width: "114px",
              }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

OemAdminTargetingMmyPopUp.propTypes = {
  mmyData: PropTypes.arrayOf(
    PropTypes.shape({
      make: PropTypes.string,
      models: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          years: PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          ),
        })
      ),
    })
  ),
  mmyInclusion: PropTypes.arrayOf(PropTypes.string),
  mmyExclusion: PropTypes.arrayOf(PropTypes.string),
  mmySave: PropTypes.func,
  onClose: PropTypes.func,
  reviewStatus: PropTypes.any,
};
